import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  AddressesSection,
  BaseForm,
  buildCustomFieldSelection,
  CustomFieldsSection,
  FormTag,
  SectionCard,
} from "components/BaseForm";
import { formatError } from "helpers/formatError";
import { useCaseMutations } from "hooks/useCase";
import { SUCCESS_MESSAGE, TAG_MODEL_TYPE } from "routes/case/form/constants";
import { buildDefaultValues } from "routes/case/form/defaults";
import { toCasePayload } from "routes/case/form/payload";
import { buildCaseSchema } from "routes/case/form/schema";
import DetailsSection from "routes/case/form/sections/DetailsSection";

// The API renders an unset nested record as an object of nulls.
const toDisplay = record =>
  record?.id != null ? { id: record.id, label: record.name } : undefined;

/**
 * The Case edit form. Its caller loads every input to the defaults before
 * mounting it, because BaseForm reads them once.
 */
const CaseForm = ({
  record,
  customFieldConfigs,
  customFieldGroups = [],
  onSaved,
  onCancel,
}) => {
  const { update } = useCaseMutations();

  const [customFieldSelection, setCustomFieldSelection] = useState(() =>
    buildCustomFieldSelection(customFieldConfigs, record.custom_fields ?? {})
  );

  const schema = useMemo(
    () => buildCaseSchema({ customFieldConfigs }),
    [customFieldConfigs]
  );

  const defaultValues = useMemo(
    () => buildDefaultValues({ record, customFieldConfigs }),
    [record, customFieldConfigs]
  );

  const investigatorsDisplay = useMemo(
    () => (record.case_investigators ?? []).map(toDisplay),
    [record]
  );

  const handleSubmit = async values => {
    const data = toCasePayload(values, { customFieldConfigs });
    try {
      await update.mutateAsync({ id: record.id, data });
    } catch (error) {
      // BaseForm shows the banner and field errors from the rethrown error.
      toast.error(formatError(error, "Could not save the Case"));
      throw error;
    }
  };

  const handleSuccess = () => {
    toast.success(SUCCESS_MESSAGE);
    onSaved();
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={onCancel}
    >
      <div className="tw:flex tw:flex-col tw:gap-6">
        <DetailsSection
          entityDisplay={toDisplay(record.entity)}
          investigatorsDisplay={investigatorsDisplay}
        />
        <AddressesSection />
        <SectionCard title="Tags">
          <div className="tw:max-w-2xl">
            <FormTag name="tags" label={null} modelType={TAG_MODEL_TYPE} />
          </div>
        </SectionCard>
        {customFieldSelection.length > 0 ? (
          <CustomFieldsSection
            selection={customFieldSelection}
            setSelection={setCustomFieldSelection}
            groups={customFieldGroups}
          />
        ) : null}
      </div>
    </BaseForm>
  );
};

CaseForm.propTypes = {
  record: PropTypes.object.isRequired,
  customFieldConfigs: PropTypes.array.isRequired,
  customFieldGroups: PropTypes.array,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default CaseForm;
